import type { AvailableVariable } from '@fluenceur/ui';

interface LexicalNode {
  type: string;
  text?: string;
  variableKey?: string;
  children?: LexicalNode[];
}

interface LexicalJson {
  root?: LexicalNode;
}

/**
 * Extract all variable keys from a Lexical JSON structure
 * @param lexicalJson - The Lexical editor state as a JSON string or object
 * @returns Array of unique variable keys found in the content
 */
export function extractVariablesFromLexicalJson(lexicalJson: string | object): string[] {
  const variables: string[] = [];

  let parsed: LexicalJson;
  try {
    parsed = typeof lexicalJson === 'string' ? JSON.parse(lexicalJson) : lexicalJson;
  } catch {
    return [];
  }

  const processNode = (node: LexicalNode): void => {
    if (node.type === 'variable' && node.variableKey) {
      variables.push(node.variableKey);
    }

    if (node.children && Array.isArray(node.children)) {
      node.children.forEach(processNode);
    }
  };

  if (parsed.root) {
    processNode(parsed.root);
  }

  return [...new Set(variables)]; // Return unique variables
}

export interface ValidationResult {
  isValid: boolean;
  missingVariables: string[];
}

/**
 * Validate that all required variables are present in the Lexical content
 * @param lexicalJson - The Lexical editor state as a JSON string or object
 * @param availableVariables - Array of available variables with their metadata
 * @returns Validation result with isValid flag and list of missing required variables
 */
export function validateRequiredVariables(
  lexicalJson: string | object,
  availableVariables: AvailableVariable[]
): ValidationResult {
  const usedVariables = extractVariablesFromLexicalJson(lexicalJson);
  const requiredVariables = availableVariables.filter((v) => v.required);
  const missingVariables: string[] = [];

  for (const variable of requiredVariables) {
    if (!usedVariables.includes(variable.key)) {
      missingVariables.push(variable.key);
    }
  }

  return {
    isValid: missingVariables.length === 0,
    missingVariables,
  };
}

export function lexicalJsonToPlainText(lexicalJson: string | object): string {
  let parsed: LexicalJson;
  try {
    parsed = typeof lexicalJson === 'string' ? JSON.parse(lexicalJson) : lexicalJson;
  } catch {
    return '';
  }

  const rootChildren = parsed.root?.children;
  if (!rootChildren || !Array.isArray(rootChildren)) {
    return '';
  }

  const nodeText = (node: LexicalNode): string => {
    if (node.type === 'text') {
      return node.text ?? '';
    }

    if (node.type === 'variable' && node.variableKey) {
      return `{{${node.variableKey}}}`;
    }

    if (!node.children || !Array.isArray(node.children)) {
      return '';
    }

    return node.children.map(nodeText).join('');
  };

  return rootChildren.map(nodeText).join('\n').trim();
}

/** Drops the blank lines at either end of a block, keeping the ones between. */
function withoutBlankEdges(lines: string[]): string[] {
  let start = 0;
  let end = lines.length;
  while (start < end && lines[start].trim() === '') start += 1;
  while (end > start && lines[end - 1].trim() === '') end -= 1;
  return lines.slice(start, end);
}

/**
 * Reads a plain-text message the way the person who typed it meant it: a blank
 * line ends a paragraph, a lone newline is a line break inside one.
 *
 * Both seeding paths below go through it so a draft looks the same in the
 * editor and in the message that leaves. Splitting on every newline instead
 * would give each blank line a block of its own, and a paragraph break would
 * read as two.
 *
 * Carriage returns and whitespace-only lines count as blank — a draft assembled
 * by joining lines is not always typed by hand — and blank lines at either end
 * are dropped, so a message never opens or closes on an empty paragraph.
 *
 * @returns one entry per paragraph, holding that paragraph's lines.
 */
function splitIntoParagraphLines(text: string): string[][] {
  return text
    .replace(/\r\n?/g, '\n')
    .split(/\n[^\S\n]*\n+/)
    .map((block) => withoutBlankEdges(block.split('\n')))
    .filter((lines) => lines.length > 0);
}

/**
 * Turns a plain-text message into the editor state the body editor seeds from.
 *
 * One block per paragraph and a line break for a lone newline, so a draft
 * written as text reaches the brand looking the way it was written. Only text
 * and line-break nodes are produced: a seeded draft carries no variable chips,
 * because the message is already addressed to one named person by the time it
 * gets here.
 */
export function plainTextToLexicalJson(text: string): string {
  const textNode = (line: string) => ({
    type: 'text',
    version: 1,
    detail: 0,
    format: 0,
    mode: 'normal',
    style: '',
    text: line,
  });

  const paragraph = (lines: string[]) => ({
    type: 'paragraph',
    version: 1,
    direction: 'ltr',
    format: '',
    indent: 0,
    textFormat: 0,
    textStyle: '',
    children: lines.flatMap((line, index) =>
      index === 0 ? [textNode(line)] : [{ type: 'linebreak', version: 1 }, textNode(line)]
    ),
  });

  // An empty message still needs a block, or the editor has nowhere to type.
  const paragraphs = splitIntoParagraphLines(text);
  const blocks = paragraphs.length > 0 ? paragraphs : [[]];

  return JSON.stringify({
    root: {
      type: 'root',
      version: 1,
      direction: 'ltr',
      format: '',
      indent: 0,
      children: blocks.map(paragraph),
    },
  });
}

/** The same message as the HTML the composer sends. */
export function plainTextToEmailHtml(text: string): string {
  const escape = (line: string) =>
    line.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;');

  return splitIntoParagraphLines(text)
    .map((lines) => `<p>${lines.map(escape).join('<br />')}</p>`)
    .join('');
}
